import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Search, Download, Printer, ChevronLeft, Phone, Mail, MapPin, MessageSquare } from "lucide-react";
import axios from "axios";

const API = '/api';

/**
 * Register Interest (Enquiries) List Page
 */
export default function KyleRiseInterests() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchInterests(); }, [page, statusFilter]);

  const fetchInterests = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/interests?page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await axios.get(url);
      setInterests(res.data.interests || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/kyle-rise/interests/${id}/status?status=${status}`);
      fetchInterests();
    } catch (err) { console.error(err); }
  };

  const exportCSV = () => {
    const headers = ["ID", "Company", "Contact", "Email", "Phone", "Location", "Spaces", "Status", "Created"];
    const rows = interests.map(i => [i.interest_id, i.company_name, i.contact_name, i.email, i.phone, i.location, i.num_spaces || "", i.status, new Date(i.created_at).toLocaleDateString()]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `enquiries-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-blue-100 text-blue-700";
      case "contacted": return "bg-yellow-100 text-yellow-700";
      case "converted": return "bg-green-100 text-green-700";
      case "closed": return "bg-gray-100 text-gray-600";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-interests">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/kyle-rise")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Register Interest</h1><p className="text-sm text-gray-500">{total} enquiries</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchInterests()} placeholder="Search company or contact..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Spaces</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>)
              : interests.length === 0 ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No enquiries found</td></tr>)
              : interests.map((interest) => (
                <tr key={interest.interest_id} onClick={() => navigate(`/admin/kyle-rise/interests/${interest.interest_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{interest.company_name}</p>
                    <p className="text-xs text-gray-500">{interest.interest_id}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{interest.contact_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{interest.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{interest.location}</td>
                  <td className="px-4 py-3 text-sm font-medium">{interest.num_spaces || "-"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(interest.status)}`}>{interest.status}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(interest.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      {interest.status === "new" && (
                        <button onClick={(e) => updateStatus(interest.interest_id, "contacted", e)} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200">Mark Contacted</button>
                      )}
                      {interest.status === "contacted" && (
                        <button onClick={(e) => updateStatus(interest.interest_id, "converted", e)} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Convert</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
}
